import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { ScrollToService } from '@df/dom/scroll/scroll-to.service';
import { ApplePay } from '@df/module-apple-pay/apple-pay.namespace';
import { IAddressData } from '@df/session/api/address-book/address';
import each from 'lodash-es/each';
import { MinibasketFastCheckoutFormComponent } from 'projects/app-default/src/app/checkout/fast/minibasket-fast-checkout-form.component';
import { MinibasketFastCheckoutStepComponent } from 'projects/app-default/src/app/checkout/fast/minibasket-fast-checkout-step.component';
import { ResultComponent } from '../../../../../ui/api/result.component';
import { OpayoApplePayButtonComponent } from '../opayo-apple-pay-button';

const SHIPPING_METHOD_SEPARATOR = 'shippingseparator';

@Component({
    selector: 'opayo-apple-pay-button-express',
    templateUrl: '../opayo-apple-pay-button.html',
    styleUrl: '../opayo-apple-pay-button.scss',
    imports: [ResultComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OpayoApplePayButtonExpressComponent extends OpayoApplePayButtonComponent {
    override isExpress: boolean = true;

    constructor(
        checkoutFormComponent: MinibasketFastCheckoutFormComponent,
        changeDetectorRef: ChangeDetectorRef,
        scrollToService: ScrollToService,
        checkoutStepComponent: MinibasketFastCheckoutStepComponent
    ) {
        super(checkoutFormComponent, changeDetectorRef, scrollToService, checkoutStepComponent);
    }
    /**
     * Converts apple contact to address
     */
    private getAddressFromContact(contact?: ApplePay.IContact): IAddressData | undefined {
        return contact ? this.applePayService.getAddressFromContact(contact) : undefined;
    }

    /**
     * Returns checkout submission data
     */
    protected override getSubmissionData(paymentAuthorizedEvent: ApplePay.IPaymentEvent): Checkout.ISubmissionData {
        const { phoneNumber, emailAddress } = paymentAuthorizedEvent.payment.shippingContact as ApplePay.IContact;
        const billingAddress = this.getAddressFromContact({ ...paymentAuthorizedEvent.payment.billingContact, phoneNumber });
        const shippingAddress = this.checkoutFormComponent.isVirtual
            ? undefined
            : this.getAddressFromContact(paymentAuthorizedEvent.payment.shippingContact);

        return {
            quote_id: this.checkoutFormComponent['backendData'].quote.quote_id,
            billing_address: billingAddress,
            shipping_address: shippingAddress,
            email: emailAddress,
            custom: this.checkoutFormComponent['data'].custom,
            paymentMethod: {
                method: this.methodCode
            }
        };
    }

    /**
     * Adds apple pay session instance events callbacks
     */
    protected override addApplePaySessionInstanceEvents(applePaySessionInstance: ApplePay.ISessionInstance): void {
        super.addApplePaySessionInstanceEvents(applePaySessionInstance);
        applePaySessionInstance.onpaymentmethodselected = (event: ApplePay.IPaymentMethodSelectedEvent) => {
            this.onPaymentMethodSelected(event, applePaySessionInstance);
        };

        applePaySessionInstance.onshippingmethodselected = (event: ApplePay.IShippingMethodSelectedEvent) => {
            this.onShippingMethodSelected(event, applePaySessionInstance);
        };

        applePaySessionInstance.onshippingcontactselected = (event: ApplePay.IShippingContactSelectedEvent) => {
            this.onShippingContactSelected(event, applePaySessionInstance);
        };
    }

    protected onPaymentMethodSelected(event: ApplePay.IPaymentMethodSelectedEvent, session: ApplePay.ISessionInstance): void {
        const totalLine = this.getTotalLine();
        if (totalLine) {
            session.completePaymentMethodSelection({
                newTotal: totalLine,
                newLineItems: this.getLineItems()
            });
        }
    }

    protected onShippingMethodSelected(event: ApplePay.IShippingMethodSelectedEvent, session: ApplePay.ISessionInstance): void {
        if (this.isVirtual()) {
            if (this.inProgressStatus.busy) {
                session.completeShippingMethodSelection(this.getShippingMethodUpdate());
            }
        } else {
            const shippingMethod = event.shippingMethod;
            const currentlyAvailable = !!this.lastShippingMethods?.find(i => i.identifier === shippingMethod.identifier);
            if (currentlyAvailable) {
                const shippingMethodCode = shippingMethod ? this.opayoApplePayService.getShippingMethodCodeFromIdentifier(shippingMethod.identifier) : '';

                this.selectShippingMethod(shippingMethodCode);
                const complete = () => {
                    if (this.inProgressStatus.busy) {
                        session.completeShippingMethodSelection(this.getShippingMethodUpdate());
                    }
                };
                (<any>this.checkoutFormComponent)._saveShippingMethod(true).then(complete).catch(complete);
            } else if (this.inProgressStatus.busy) {
                session.completeShippingMethodSelection(this.getShippingMethodUpdate());
            }
        }
    }


        /**
     * Returns shipping method update
     * @returns {ApplePay.IShippingMethodUpdate}
     */
        protected getShippingMethodUpdate(): ApplePay.IShippingMethodUpdate {
            return {
                newTotal: this.getTotalLine(),
                newLineItems: this.getLineItems()
            };
        }

    private onShippingContactSelected(event: ApplePay.IShippingContactSelectedEvent, session: ApplePay.ISessionInstance): void {
        if (this.isVirtual()) {
            if (this.inProgressStatus.busy) {
                session.completeShippingMethodSelection(this.getShippingContactUpdate([]));
            }
        } else {
            const shippingContact = event.shippingContact;
            const shippingAddress = this.applePayService.getAddressFromContact(shippingContact);
            if (!this.checkoutFormComponent['data'].address.shipping) {
                this.checkoutFormComponent['data'].address.shipping = {};
            }
            const shippingData = this.checkoutFormComponent['data'].address.shipping;
            each(shippingData, (value, key) => {
                delete shippingData[key];
            });

            each(shippingAddress, (value, key) => {
                shippingData[key] = value;
            });
            const complete = () => {
                const methodsUpdateRequired = this.shippingMethodsUpdateRequired();
                if (!methodsUpdateRequired) {
                    if (this.inProgressStatus.busy) {
                        session.completeShippingContactSelection(this.getShippingContactUpdate());
                    }
                } else {
                    const newMethods = this.getShippingMethods();
                    this.lastShippingMethods = newMethods;
                    const completeMethod = () => {
                        if (this.inProgressStatus.busy) {
                            session.completeShippingContactSelection(this.getShippingContactUpdate(newMethods));
                        }
                    };
                    const shippingMethod = newMethods?.[0];
                    const shippingMethodCode = shippingMethod
                        ? this.opayoApplePayService.getShippingMethodCodeFromIdentifier(shippingMethod.identifier)
                        : '';
                    this.selectShippingMethod(shippingMethodCode);
                    (<any>this.checkoutFormComponent)._saveShippingMethod(true).then(completeMethod).catch(completeMethod);
                }
            };
            (<any>this.checkoutFormComponent)._saveShippingAddress(true, complete, complete);
        }
    }

    /**
     * Returns shipping contact update
     * @returns {ApplePay.IShippingContactUpdate}
     */
    protected getShippingContactUpdate(newMethods?: ApplePay.IShippingMethod[]): ApplePay.IShippingContactUpdate {
        const update: ApplePay.IShippingContactUpdate = {
            newTotal: this.getTotalLine(),
            newLineItems: this.getLineItems()
        };
        if (newMethods) {
            update.newShippingMethods = newMethods;
        }

        return update;
    }

    /**
     * Sets new shipping method
     * @param method
     */
    protected selectShippingMethod(method?: string): void {
        this.checkoutFormComponent['data'].shipping.method = method;
        delete this.checkoutFormComponent['data'].shipping.delivery_date;
        const additionalData = this.checkoutFormComponent['data'].shipping.additionalData;
        if (additionalData) {
            each(additionalData, (value, key) => {
                delete additionalData[key];
            });
        }
    }

    /**
     * Checks if shipping methods should be updated
     * @returns {boolean}
     */
    protected shippingMethodsUpdateRequired(): boolean {
        if (this.isVirtual()) {
            return false;
        }

        if (!this.lastShippingMethods) {
            return true;
        }

        const lastShippingMethodsWithCodes = this.lastShippingMethods.map(i => {
            return { ...i, identifier: this.opayoApplePayService.getShippingMethodCodeFromIdentifier(i.identifier) };
        });
        const shippingMethodsWithCodes = this.getShippingMethods()?.map(i => {
            return { ...i, identifier: this.opayoApplePayService.getShippingMethodCodeFromIdentifier(i.identifier) };
        });

        return JSON.stringify(lastShippingMethodsWithCodes) !== JSON.stringify(shippingMethodsWithCodes);
    }

    protected override getTotalLine(type = ApplePay.LineItemType.final): ApplePay.ILineItem | undefined {
        const total = this.app.customer.basket.totals?.grand_total;
        if (total) {
            return {
                label: 'Oliver Bonas',
                amount: total.toString(),
                type
            };
        }
        return undefined;
    }

    protected override getLineItems(): ApplePay.ILineItem[] {
        const items: ApplePay.ILineItem[] = [];
        const basket = this.app.customer.basket;

        if (basket.items) {
            basket.items.forEach(item => {
                const label = (item as { name?: string }).name || (item as { product?: { name?: string } }).product?.name || 'Item';
                items.push({
                    label,
                    amount: ((item as { row_total?: number }).row_total || 0).toString(),
                    type: ApplePay.LineItemType.final
                });
            });
        }

        return items;
    }

    protected override getShippingMethods(): ApplePay.IShippingMethod[] {
        const shippingMethods = this.checkoutFormComponent['nonCollectionShippingMethods'] || [];
        const methods: ApplePay.IShippingMethod[] = [];
        if (shippingMethods && Array.isArray(shippingMethods)) {
            shippingMethods.forEach((method: any) => {
                methods.push(this.opayoApplePayService.mapShippingMethod(method));
            });
        }

        return methods;
    }
}
